import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Search, User as UserIcon, Plus, Pencil, Trash2, ShieldPlus } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminAllUsers,
  useCreateSuperadmin,
  usePatchUserGlobal,
  useDeleteUserGlobal,
} from "@/hooks/use-admin";
import { useMe } from "@/hooks/use-me";
import type { AdminUserWithAccountOut } from "@/lib/api";

export const Route = createFileRoute("/_auth/admin/users")({
  component: AdminUsersPage,
});

const ROLES = ["owner", "admin", "editor", "analyst", "responder", "viewer", "superadmin"] as const;

function AdminUsersPage() {
  const [q, setQ] = useState("");
  const [showNewSuper, setShowNewSuper] = useState(false);
  const [editing, setEditing] = useState<AdminUserWithAccountOut | null>(null);
  const users = useAdminAllUsers({ q: q || undefined });

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 mb-2">
            <ArrowLeft size={14} /> Volver
          </Link>
          <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Usuarios</h1>
          <p className="text-sm text-ink-500 mt-1">Todos los usuarios de la plataforma, con su tenant asociado.</p>
        </div>
        <Button onClick={() => setShowNewSuper(true)}>
          <ShieldPlus size={14} /> Nuevo superadmin
        </Button>
      </header>

      <Card className="mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input
            className="pl-10"
            placeholder="Buscar por email o nombre…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Usuarios</CardTitle>
          <CardDescription>
            {users.data ? `${users.data.length} resultados` : "Cargando…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
          {users.isLoading ? (
            Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 mb-2" />)
          ) : !users.data || users.data.length === 0 ? (
            <p className="text-sm text-ink-500 py-6 text-center">Sin usuarios.</p>
          ) : (
            users.data.map((u) => <UserRow key={u.id} u={u} onEdit={() => setEditing(u)} />)
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-ink-500 mt-3">
        Para crear usuarios dentro de un tenant específico, entra a{" "}
        <Link to="/admin" className="text-brand-600 hover:underline">Cuentas</Link> → selecciona cuenta → pestaña Users.
      </p>

      {showNewSuper && <NewSuperadminModal onClose={() => setShowNewSuper(false)} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function UserRow({ u, onEdit }: { u: AdminUserWithAccountOut; onEdit: () => void }) {
  const { data: me } = useMe();
  const del = useDeleteUserGlobal();
  const isSelf = me?.id === u.id;

  return (
    <div className="py-3 flex items-center gap-3">
      <UserIcon size={18} className="text-ink-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-ink-900 dark:text-white">{u.name}</span>
          <span className="text-xs text-ink-500 font-mono">{u.email}</span>
          <Badge variant={u.role === "superadmin" ? "warn" : u.role === "owner" ? "good" : "muted"}>
            {u.role}
          </Badge>
          <Badge variant={u.status === "active" ? "good" : "bad"}>{u.status}</Badge>
          {isSelf && <Badge variant="muted">tú</Badge>}
        </div>
        <p className="text-xs text-ink-500 mt-1">
          {u.account_id ? (
            <Link
              to="/admin/accounts/$accountId"
              params={{ accountId: String(u.account_id) }}
              className="text-brand-600 hover:underline"
            >
              {u.account_name ?? `Cuenta #${u.account_id}`}
            </Link>
          ) : (
            <span className="italic">sin cuenta (plataforma)</span>
          )}
          {" · último login "}
          {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("es-PE") : "nunca"}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-[11px] text-ink-500 font-mono mr-2">#{u.id}</span>
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Pencil size={12} /> Editar
        </Button>
        <button
          type="button"
          disabled={isSelf}
          onClick={() => {
            if (confirm(`¿Eliminar usuario "${u.email}"? Esto cancela todas sus sesiones.`)) {
              del.mutate(u.id);
            }
          }}
          className="w-8 h-8 rounded-md hover:bg-bad/10 text-bad disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
          aria-label="Eliminar usuario"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

const superadminSchema = z.object({
  name: z.string().min(2, "Mínimo 2 caracteres").max(160),
  email: z.string().email("Email inválido"),
  password: z.string().min(8, "Mínimo 8 caracteres"),
});
type SuperadminInput = z.infer<typeof superadminSchema>;

function NewSuperadminModal({ onClose }: { onClose: () => void }) {
  const create = useCreateSuperadmin();
  const form = useForm<SuperadminInput>({
    resolver: zodResolver(superadminSchema),
    defaultValues: { name: "", email: "", password: "" },
  });

  async function onSubmit(data: SuperadminInput) {
    try {
      await create.mutateAsync(data);
      onClose();
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Nuevo superadmin</CardTitle>
          <CardDescription>
            Crea un operador de plataforma sin tenant asignado. Tendrá acceso a todos los datos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input className="mt-1" {...form.register("name")} placeholder="Nombre completo" />
              {form.formState.errors.name && (
                <p className="text-xs text-bad mt-1">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" className="mt-1" {...form.register("email")} placeholder="ops@empresa.com" />
              {form.formState.errors.email && (
                <p className="text-xs text-bad mt-1">{form.formState.errors.email.message}</p>
              )}
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input type="password" className="mt-1" {...form.register("password")} />
              {form.formState.errors.password && (
                <p className="text-xs text-bad mt-1">{form.formState.errors.password.message}</p>
              )}
              <p className="text-[11px] text-ink-500 mt-1">Mínimo 8 caracteres, letras y números.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creando…" : "Crear superadmin"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

const editSchema = z.object({
  name: z.string().min(2).max(160),
  email: z.string().email(),
  role: z.enum(ROLES),
  status: z.enum(["active", "suspended"]),
  password: z.string().optional(),
});
type EditInput = z.infer<typeof editSchema>;

function EditUserModal({ user, onClose }: { user: AdminUserWithAccountOut; onClose: () => void }) {
  const patch = usePatchUserGlobal();
  const form = useForm<EditInput>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: user.name,
      email: user.email,
      role: (user.role as typeof ROLES[number]) ?? "viewer",
      status: (user.status as "active" | "suspended") ?? "active",
      password: "",
    },
  });

  async function onSubmit(data: EditInput) {
    const body: Record<string, string> = {
      name: data.name,
      email: data.email,
      role: data.role,
      status: data.status,
    };
    if (data.password && data.password.length >= 8) {
      body.password = data.password;
    }
    try {
      await patch.mutateAsync({ userId: user.id, body });
      onClose();
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Editar usuario</CardTitle>
          <CardDescription>
            #{user.id} · {user.account_id ? `Tenant ${user.account_name}` : "Superadmin plataforma"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input className="mt-1" {...form.register("name")} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" className="mt-1" {...form.register("email")} />
              {form.formState.errors.email && (
                <p className="text-xs text-bad mt-1">{form.formState.errors.email.message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rol</Label>
                <select
                  className="mt-1 h-10 w-full rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 text-sm"
                  {...form.register("role")}
                >
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label>Estado</Label>
                <select
                  className="mt-1 h-10 w-full rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 text-sm"
                  {...form.register("status")}
                >
                  <option value="active">active</option>
                  <option value="suspended">suspended</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Nueva contraseña (opcional)</Label>
              <Input type="password" className="mt-1" {...form.register("password")} placeholder="Dejar vacío para mantener" />
              <p className="text-[11px] text-ink-500 mt-1">Mínimo 8 caracteres, letras + números.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={patch.isPending}>
                {patch.isPending ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
